// Which setting groups the user keeps open, remembered between sessions.
// Someone who edits colours all day should not have to re-open the Appearance
// group on every section they click.
//
// Group ids are namespaced ('section:appearance', 'general:website') because
// the section panel and the General tab have their own sets of groups but
// share this one entry. A group with no stored preference falls back to the
// screen's own default.

const STORAGE_KEY = 'wb_builder_open_setting_groups'

type OpenGroups = Record<string, boolean>

export function loadOpenGroups (): OpenGroups {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    const parsed = stored ? JSON.parse(stored) : null

    return (parsed && typeof parsed === 'object') ? parsed as OpenGroups : {}
  } catch (error) {
    // A private-mode browser or a hand-edited entry must not break the editor.
    return {}
  }
}

export function saveOpenGroups (groups: OpenGroups): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(groups))
  } catch (error) {
    // Nothing to do: the panels still work, they just start at the default.
  }
}

// Indexes of the panels to open: the stored preference where there is one,
// the screen's default for groups the user has never touched.
export function openPanelIndexes (groupIds: Array<string>, isOpenByDefault: (index: number) => boolean): Array<number> {
  const stored = loadOpenGroups()

  return groupIds
    .map((groupId: string, index: number) => {
      const remembered = stored[groupId]
      return (typeof remembered === 'boolean' ? remembered : isOpenByDefault(index)) ? index : -1
    })
    .filter((index: number) => index >= 0)
}

export function rememberOpenPanels (groupIds: Array<string>, openIndexes: Array<number>): void {
  const groups = loadOpenGroups()

  groupIds.forEach((groupId: string, index: number) => {
    groups[groupId] = openIndexes.indexOf(index) >= 0
  })

  saveOpenGroups(groups)
}

// The three states a settings panel can be in. A search opens every group it
// still shows, a section whose settings all land in one group renders that
// group without a header (so it must stay open), and otherwise the stored
// preference decides, falling back to all collapsed.
export function panelsToOpen (
  groupIds: Array<string>,
  options: { filtering: boolean; isOpenByDefault?: (index: number) => boolean }): Array<number> {
  if (options.filtering || groupIds.length === 1) {
    return groupIds.map((groupId: string, index: number) => index)
  }

  return openPanelIndexes(groupIds, options.isOpenByDefault || (() => false))
}
